export interface RunWithAutoRetryOptions {
  delayMs?: number;
  maxAttempts?: number;
  onRetry?: (attempt: number, error: unknown) => void;
  onCountdown?: (secondsLeft: number) => void;
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function countdownDelay(
  delayMs: number,
  onCountdown?: (secondsLeft: number) => void
) {
  const totalSeconds = Math.ceil(delayMs / 1000);
  for (let secondsLeft = totalSeconds; secondsLeft > 0; secondsLeft -= 1) {
    onCountdown?.(secondsLeft);
    await sleep(1000);
  }
}

/**
 * Runs fn until it succeeds or maxAttempts is reached.
 * On failure waits delayMs (with optional countdown ticks) before retrying.
 */
export async function runWithAutoRetry<T>(
  fn: () => Promise<T>,
  options: RunWithAutoRetryOptions = {}
): Promise<T> {
  const delayMs = options.delayMs ?? 5000;
  const maxAttempts = options.maxAttempts ?? 10;
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt >= maxAttempts) break;
      options.onRetry?.(attempt, error);
      await countdownDelay(delayMs, options.onCountdown);
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error(String(lastError ?? "操作失败"));
}
